import { useRef, type ReactElement } from "react";
import css from "./ChannelPicker.module.css";

/**
 * A15 channel picker — 3-segment toggle per screen-03 эталон.
 *
 * Visual: each cell is either ICON or TEXT, NOT both (per эталон):
 *  - TG  → paper-plane SVG icon ONLY
 *  - VK  → "VK" text only (Orbitron 11/800/0.10em)
 *  - MAX → "MAX" text only (same typography as VK)
 *
 * Active cell: cyan border + cyan-tinted bg + cyan halo + filter drop-shadow.
 * Toggle: clicking the active cell deselects it (channel becomes "").
 *
 * Compact mode (used on cards in B.5) keeps the same icon/text scheme but
 * shrinks cells to 22 tall.
 */
export type Channel = "" | "tg" | "vk" | "max";

export interface ChannelPickerProps {
  value: Channel;
  onChange: (next: Channel) => void;
  /** Compact icon-only variant — used on cards (no labels, smaller cells). */
  compact?: boolean;
  disabled?: boolean;
  /** Long-press (450мс) по ячейке — аддитивно (25.08: «Открыть переписку» у ✈); гасит следующий click. */
  onCellLongPress?: (id: Exclude<Channel, "">) => void;
}

interface ChannelOption {
  id: Exclude<Channel, "">;
  kind: "icon" | "text";
  label: string;
  icon: ReactElement | null;
}

const TG_ICON = (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M16 2.5 L1.5 8 L6.5 10 L13 5 L8 11.5 L14.5 15.5 Z" />
  </svg>
);

const OPTIONS: ChannelOption[] = [
  { id: "tg",  kind: "icon", label: "TG",  icon: TG_ICON },
  { id: "vk",  kind: "text", label: "VK",  icon: null },
  { id: "max", kind: "text", label: "MAX", icon: null },
];

export function ChannelPicker({ value, onChange, compact, disabled, onCellLongPress }: ChannelPickerProps) {
  const lpT = useRef(0); const lpFired = useRef(false); const lpStart = useRef<{ x: number; y: number } | null>(null);
  return (
    <div
      className={`${css.row} ${compact ? css.rowCompact : ""}`}
      role="radiogroup"
      aria-label="Канал связи"
    >
      {OPTIONS.map((opt) => {
        const active = value === opt.id;
        return (
          <button
            key={opt.id}
            type="button"
            role="radio"
            aria-checked={active}
            aria-label={opt.label}
            disabled={disabled}
            className={`${css.cell} ${active ? css.active : ""}`}
            onClick={() => { if (lpFired.current) { lpFired.current = false; return; } if (!disabled) onChange(active ? "" : opt.id); }}
            onPointerDown={(e) => {
              if (!onCellLongPress || disabled) return;
              lpFired.current = false; lpStart.current = { x: e.clientX, y: e.clientY };
              lpT.current = window.setTimeout(() => { lpFired.current = true; onCellLongPress(opt.id); }, 450);
            }}
            onPointerUp={() => window.clearTimeout(lpT.current)}
            /* палец дрожит на 1-2px — отмена только при реальном сдвиге >8px (паттерн AdminStrip) */
            onPointerMove={(e) => { const s = lpStart.current; if (s && (Math.abs(e.clientX - s.x) > 8 || Math.abs(e.clientY - s.y) > 8)) window.clearTimeout(lpT.current); }}
            onPointerLeave={() => window.clearTimeout(lpT.current)}
            onContextMenu={(e) => { if (onCellLongPress) e.preventDefault(); }}
          >
            {opt.kind === "icon" ? <span className={css.icon}>{opt.icon}</span> : <span className={css.label}>{opt.label}</span>}
          </button>
        );
      })}
    </div>
  );
}
